import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CommonPage, Content, InRow, LoadableContent, PageTitle, ResponsiveGap } from "common/Grid";
import { toast } from "react-toastify";
import { useGetFileUploadMutation, useGetInquiryByIdQuery, useUpdateInquiryMutation } from "store/action/inquiryApi";
import { MainForm } from "../Mainform/MainForm";
import { PrimaryButton, SecondaryButton } from "common/Forms";
import { captureComponentError } from "utils/sentry";

export const Page = () => {
  const { inquiryId } = useParams();
  const navigate = useNavigate();
  const [pageLoading, setPageLoading] = useState(false);
  const [validationResult, setValidationResult] = useState({});
  const isValid = Object.values(validationResult).every(x => x)
  const { data: inquiryData, isLoading: isFetching, isError } = useGetInquiryByIdQuery(inquiryId, {
    skip: !inquiryId,
    refetchOnMountOrArgChange: true,
  });
  const [uploadFile] = useGetFileUploadMutation();

  const [updateInquiry, { isLoading: isUpdating }] = useUpdateInquiryMutation();

  const [userInput, setUserInput] = useState({
    logoUrl: "",
    favicon: '',
    name: "",
    surname: "",
    businessEmail: "",
    businessTradingLocation: "",
    businessMobileNumber: "",
    businessCategory: "",
    businessSubcategory: "",
    businessTradingName: "",
    businessType: "",
    website: "",
    bankName: "",
    accountNumber: "",
    instagramProfile: "",
    facebookProfile: "",
    mainColor: "",
    secondaryColor: "",
  });

  useEffect(() => {
    if (inquiryData?.data) {
      setUserInput(inquiryData.data);
    }
  }, [inquiryData]);

  const handleSubmit = async () => {
    try {
      setPageLoading(true);
      let logoUrl = userInput.logoUrl?.url || userInput.logoUrl;
      let favicon = userInput.favicon?.url || userInput.favicon;

      const getFileType = (dataUrl) => {
        if (!dataUrl || typeof dataUrl !== "string") return "png";
        const mime = dataUrl.split(";")[0].split(":")[1] || "";
        if (mime.includes("icon") || mime.includes("x-icon") || mime.includes("vnd.microsoft.icon")) return "ico";
        if (mime.includes("jpeg")) return "jpg";
        return mime.split("/")[1] || "png";
      };

      if (typeof logoUrl === "string" && logoUrl.startsWith("data:")) {
        const logoUploadResponse = await uploadFile({
          base64: logoUrl,
          type: getFileType(logoUrl),
        }).unwrap();
        if (logoUploadResponse?.status === "SUCCESS") {
          logoUrl = logoUploadResponse.data?.url || logoUploadResponse.data;
        } else {
          toast.error("Logo upload failed");
          setPageLoading(false);
          return;
        }
      }

      if (typeof favicon === "string" && favicon.startsWith("data:")) {
        const faviconUploadResponse = await uploadFile({
          base64: favicon,
          type: getFileType(favicon),
        }).unwrap();
        if (faviconUploadResponse?.status === "SUCCESS") {
          favicon = faviconUploadResponse.data?.url || faviconUploadResponse.data;
        } else {
          toast.error("Favicon upload failed");
          setPageLoading(false);
          return;
        }
      }

      const payload = {
        name: userInput.name,
        surname: userInput.surname,
        businessMobileNumber: userInput.businessMobileNumber,
        businessEmail: userInput.businessEmail,
        businessTradingName: userInput.businessTradingName,
        businessTradingLocation: userInput.businessTradingLocation,
        businessType: userInput.businessType,
        businessCategory: userInput.businessCategory,
        businessSubcategory: userInput.businessSubcategory,
        bankName: userInput.bankName,
        accountNumber: userInput.accountNumber,
        logoUrl: logoUrl,
        website: userInput.website,
        facebookProfile: userInput.facebookProfile,
        instagramProfile: userInput.instagramProfile,
        mainColor: userInput.mainColor,
        secondaryColor: userInput.secondaryColor,
        favicon: favicon,
        id: inquiryId
      }

      const response = await updateInquiry(payload).unwrap();

      if (response?.status === "SUCCESS") {
        toast.success(response?.message || "Inquiry updated successfully");
        setTimeout(() => {
          navigate("/inquiry");
          setPageLoading(false);
        }, 1500);
      } else {
        toast.error(response?.message || "Failed to update inquiry");
        setPageLoading(false);
      }
    } catch (error) {
      captureComponentError(error, { component: "EditInquiryPage", action: "handleSubmit" });
      toast.error(error?.data?.message || "An error occurred while updating the inquiry");
      setPageLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/inquiry");
  };

  return (
    <CommonPage>
      <Content>
        <PageTitle title="Edit Inquiry" />
        <LoadableContent isLoading={isFetching || isUpdating || pageLoading} isError={isError}>
          <ResponsiveGap>
            <MainForm
              userInput={userInput}
              setUserInput={setUserInput}
              setValidationResult={setValidationResult}
            />
            <ResponsiveGap left={false} right={false}>
              <InRow gap={2} classNames={['justify-center']}>
                <PrimaryButton disabled={!isValid} action={handleSubmit}>Save Changes</PrimaryButton>
                <SecondaryButton action={handleCancel}>Cancel</SecondaryButton>
              </InRow>
            </ResponsiveGap>
          </ResponsiveGap>
        </LoadableContent>
      </Content>
    </CommonPage>
  );
};

export default Page;
